import { useCallback, useRef, useState } from "react";

type UsePerspectiveCardHoverOptions = {
  maxRotation?: number;
  sensitivity?: number;
  enabled?: boolean;
};

interface MousePosition {
  x: number;
  y: number;
}

export function usePerspectiveCardHover(options: UsePerspectiveCardHoverOptions = {}) {
  const { maxRotation = 15, sensitivity = 0.5, enabled = true } = options;
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const calculateRotation = useCallback(
    (mousePos: MousePosition, rect: DOMRect) => {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const deltaX = mousePos.x - centerX;
      const deltaY = mousePos.y - centerY;

      const rotationX = -(deltaY / rect.height) * maxRotation * sensitivity;
      const rotationY = -(deltaX / rect.width) * maxRotation * sensitivity;

      return {
        x: Math.max(-maxRotation, Math.min(maxRotation, rotationX)),
        y: Math.max(-maxRotation, Math.min(maxRotation, rotationY)),
      };
    },
    [maxRotation, sensitivity],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!cardRef.current || !enabled) return;

      const rect = cardRef.current.getBoundingClientRect();
      const mousePos = { x: event.clientX, y: event.clientY };

      setMousePosition(mousePos);
      const newRotation = calculateRotation(mousePos, rect);
      setRotation(newRotation);
    },
    [calculateRotation, enabled],
  );

  const handleMouseEnter = useCallback(() => {
    if (!enabled) return;
    setIsHovered(true);
  }, [enabled]);

  const handleMouseLeave = useCallback(() => {
    if (!enabled) return;
    setIsHovered(false);
    setRotation({ x: 0, y: 0 });
  }, [enabled]);

  const getBrightness = useCallback(() => {
    if (!isHovered || !cardRef.current || !enabled) return 1;

    const rect = cardRef.current.getBoundingClientRect();
    const relativeY = (mousePosition.y - rect.top) / rect.height;

    // Map from 0-1 to 1.3-0.8 (top to bottom)
    return Math.max(0.8, Math.min(1.3, 1.3 - relativeY));
  }, [enabled, isHovered, mousePosition.y]);

  const cardStyle = {
    transform: `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) translateZ(${isHovered && enabled ? 20 : 0}px)`,
    transition: isHovered
      ? "transform .1 ease-in, filter 0.1s ease-in"
      : "transform 0.2s ease-out, filter 0.2s ease-out",
    filter: `brightness(${getBrightness()})`,
  };

  return {
    cardRef,
    cardStyle,
    isHovered,
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
  };
}
